import { Link } from '@mui/material';
import { type ComponentProps, type PropsWithChildren, type FunctionComponent } from 'react';
import { useInteractionTracking } from '@/components/Analytics/hooks/useInteractionTracking';
import { useServiceDeskLink } from '@/components/Common/SupportLink/createServiceDeskLink';
import { useSupportData } from '@/contexts/support-context';
import { typedMemo } from '@/helpers/memo';
import { useSafeClusterId } from '@/routes/_authenticated/_dashboard/accounts/$accountId/clusters/$clusterId/route.hooks';
import { useSelectedAccountId } from '@/routes/_authenticated/_dashboard/accounts/$accountId/route.hooks';

export const SupportLink = typedMemo(function SupportLink<T extends FunctionComponent>({
  children,
  component,
  ...props
}: PropsWithChildren<{ component?: T } & ComponentProps<T>>) {
  const {
    supportData: { clusterVersion },
  } = useSupportData();
  const accountId = useSelectedAccountId();
  const clusterId = useSafeClusterId();
  const url = useServiceDeskLink({
    summary: 'I need help with ...',
    description: 'Describe in detail what you need help with',
    accountId,
    clusterId,
    clusterVersion,
  });

  const trackClick = useInteractionTracking({
    details: clusterVersion ? `clusterVersion:${clusterVersion}` : undefined,
    resource_intent: 'navigate',
    resource_type: 'external-resource',
    resource_url_template: '/support',

    action_type: 'click',
    target_type: 'link',
  });

  return (
    <Link
      {...props}
      component={component}
      href={url}
      rel="noreferrer"
      target="_blank"
      underline="none"
      onClick={() => trackClick()}
    >
      {children}
    </Link>
  );
});
